import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Paper,
  Chip,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Close,
  PlayArrow,
  FolderOpen,
  School,
  AccountTree,
  ViewInAr,
  Extension,
  TrendingUp,
} from '@mui/icons-material';

interface WelcomeScreenProps {
  onNewProject: () => void;
  onBrowseTemplates: () => void;
  onTakeTour: () => void;
  onClose: () => void;
  showDontShowAgain?: boolean;
  onDontShowAgain?: () => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  onNewProject,
  onBrowseTemplates,
  onTakeTour,
  onClose,
  showDontShowAgain = true,
  onDontShowAgain,
}) => {
  const theme = useTheme();

  const quickActions = [
    {
      title: 'New Project',
      description: 'Start a new robotics project',
      icon: <PlayArrow />,
      action: onNewProject,
      color: 'primary' as const,
    },
    {
      title: 'Browse Templates',
      description: 'Explore project templates',
      icon: <FolderOpen />,
      action: onBrowseTemplates,
      color: 'secondary' as const,
    },
    {
      title: 'Take Tour',
      description: 'Learn how to use Telix Lab',
      icon: <School />,
      action: onTakeTour,
      color: 'info' as const,
    },
  ];

  const features = [
    { icon: <AccountTree />, title: 'Flow Editor', description: 'Visual programming' },
    { icon: <ViewInAr />, title: '3D Simulator', description: 'Realistic simulation' },
    { icon: <Extension />, title: 'Modules', description: 'Extensible platform' },
  ];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        zIndex: 1300,
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          p: 4,
          position: 'relative',
        }}
      >
        {/* Close Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box
            component="img"
            src="/telixia-logo-color-736px.png"
            alt="Telix Lab Logo"
            sx={{
              height: 120,
              width: 'auto',
              maxWidth: '60%',
              objectFit: 'contain',
              mb: 3,
            }}
          />
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Welcome to Telix Lab
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Virtual Lab Platform for Robotics & AI
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
            Learn robotics and AI through hands-on experimentation. Build, simulate, and deploy
            robot applications without complex setup.
          </Typography>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
            Get Started
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {quickActions.map((action, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                  onClick={action.action}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                    <Box
                      sx={{
                        fontSize: 48,
                        color: `${action.color}.main`,
                        mb: 2,
                      }}
                    >
                      {action.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {action.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {action.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button variant="contained" color={action.color} startIcon={action.icon}>
                      {action.title}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
            Key Features
          </Typography>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    height: '100%',
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'white',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 40,
                      color: 'primary.main',
                      mb: 2,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Platform Stats (if returning user) */}
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'white',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h4" color="primary.main">
                0
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Projects Created
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="primary.main">
                0
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Modules Installed
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="primary.main">
                0
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Learning Hours
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Don't Show Again */}
        {showDontShowAgain && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                onDontShowAgain?.();
                onClose();
              }}
            >
              Don't show this again
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WelcomeScreen;

